// src/ids.ts
import { v7 as uuidv7, validate as uuidValidate, version as uuidVersion } from "uuid";
function newId() {
  return uuidv7();
}
function isUuid(value) {
  return uuidValidate(value);
}
function isUuidV7(value) {
  return uuidValidate(value) && uuidVersion(value) === 7;
}

// src/units.ts
import Decimal from "decimal.js";
import { z } from "zod";
var UNITS = ["LF", "CY", "SY", "EA", "TON", "HR", "SF", "LS"];
var UnitSchema = z.enum(UNITS);
var DEFAULT_UNITS_ENABLED = ["LF", "CY", "SY", "EA", "TON", "HR"];
var UNIT_LABELS = {
  LF: "Linear feet",
  CY: "Cubic yards",
  SY: "Square yards",
  EA: "Each",
  TON: "Tons",
  HR: "Hours",
  SF: "Square feet",
  LS: "Lump sum"
};
var DECIMAL_RE = /^-?\d+(\.\d+)?$/;
var DecimalString = z.string().regex(DECIMAL_RE, "must be a decimal number as a string");
var NonNegativeDecimalString = DecimalString.refine(
  (s) => DECIMAL_RE.test(s) && new Decimal(s).gte(0),
  "must be zero or positive"
);
var HoursString = DecimalString.refine(
  (s) => {
    if (!DECIMAL_RE.test(s)) return false;
    const d = new Decimal(s);
    return d.gte(0) && d.lte(24);
  },
  "hours must be between 0 and 24"
);
function dec(value) {
  return new Decimal(value);
}
function formatQuantity(value, unit) {
  const d = new Decimal(value);
  const fixed = d.toDecimalPlaces(2).toString();
  const [whole = "0", frac] = fixed.split(".");
  const sign = whole.startsWith("-") ? "-" : "";
  const digits = whole.replace("-", "");
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body = frac ? `${sign}${grouped}.${frac}` : `${sign}${grouped}`;
  return unit ? `${body} ${unit}` : body;
}
function formatHours(value) {
  return new Decimal(value).toFixed(1);
}
function sumDecimals(values) {
  return values.reduce((acc, v) => acc.plus(new Decimal(v)), new Decimal(0));
}

// src/production.ts
import Decimal2 from "decimal.js";
var DEFAULT_BANDS = { green: 95, amber: 80 };
function percentOfPlan(actual, plan) {
  if (plan === null || plan === void 0) return null;
  const p = new Decimal2(plan);
  if (p.lte(0)) return null;
  return new Decimal2(actual).div(p).mul(100).toDecimalPlaces(0, Decimal2.ROUND_HALF_UP).toNumber();
}
function bandFor(pct, bands = DEFAULT_BANDS) {
  if (pct === null) return "none";
  if (pct >= bands.green) return "green";
  if (pct >= bands.amber) return "amber";
  return "red";
}
function productionFor(actual, plan, bands = DEFAULT_BANDS) {
  const pct = percentOfPlan(actual, plan);
  return {
    actual: new Decimal2(actual).toString(),
    plan: plan === null || plan === void 0 ? null : new Decimal2(plan).toString(),
    pct,
    band: bandFor(pct, bands)
  };
}
function plannedPerDay(plannedTotal, workingDays) {
  if (!Number.isFinite(workingDays) || workingDays <= 0) return null;
  return new Decimal2(plannedTotal).div(workingDays).toDecimalPlaces(2).toString();
}
function isSuspiciousQuantity(quantity, planPerDay) {
  if (planPerDay === null || planPerDay === void 0) return false;
  const p = new Decimal2(planPerDay);
  if (p.lte(0)) return false;
  return new Decimal2(quantity).gt(p.mul(3));
}
function estimateCost(lines) {
  return lines.reduce((acc, l) => acc.plus(new Decimal2(l.hours).mul(l.hourlyCost)), new Decimal2(0)).toDecimalPlaces(2).toString();
}

// src/schemas/common.ts
import { z as z2 } from "zod";
var Uuid = z2.string().uuid();
var IsoTimestamp = z2.string().datetime({ offset: true });
var NullableIsoTimestamp = IsoTimestamp.nullable();
var DateOnly = z2.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
function isIanaTimezone(tz) {
  if (!tz || tz.trim() !== tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(/* @__PURE__ */ new Date());
    return true;
  } catch {
    return false;
  }
}
var Timezone = z2.string().min(1).refine(isIanaTimezone, "expected an IANA timezone, e.g. America/Chicago");
var TimeOfDay = z2.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM");
var Email = z2.string().email();
var Phone = z2.string().regex(/^\+[1-9]\d{6,14}$/, "expected E.164 phone, e.g. +16155550101");
var SyncColumns = z2.object({
  syncVersion: z2.number().int().nonnegative(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
  deletedAt: NullableIsoTimestamp
});
var Paginated = (item) => z2.object({ items: z2.array(item), nextCursor: z2.string().nullable() });
var ListQuery = z2.object({
  limit: z2.coerce.number().int().min(1).max(200).default(50),
  cursor: z2.string().optional()
});
var Role = z2.enum(["owner", "admin", "foreman", "worker"]);
var JobStatus = z2.enum(["bidding", "active", "complete"]);
var EntryStatus = z2.enum([
  "draft",
  "finished",
  "needs_review",
  "approved",
  "synced"
]);
var EventType = z2.enum([
  "delay_weather",
  "delay_breakdown",
  "delay_waiting",
  "delay_utility",
  "extra_work",
  "other"
]);
var EVENT_LABELS = {
  delay_weather: "Weather",
  delay_breakdown: "Breakdown",
  delay_waiting: "Waiting on others",
  delay_utility: "Utility conflict",
  extra_work: "Extra work",
  other: "Other"
};
var WeatherCondition = z2.enum([
  "clear",
  "cloudy",
  "rain",
  "snow",
  "wind"
]);
var RevisionSource = z2.enum(["foreman", "office", "conflict"]);

// src/schemas/entities.ts
import { z as z3 } from "zod";
var ReportConfigSchema = z3.object({
  photoSize: z3.enum(["small", "large"]).default("large"),
  sections: z3.object({
    weather: z3.boolean().default(true),
    delays: z3.boolean().default(true),
    notes: z3.boolean().default(true),
    photos: z3.boolean().default(true),
    toDate: z3.boolean().default(true)
  }).default({}),
  showCostEstimates: z3.boolean().default(false),
  footerText: z3.string().max(200).default(""),
  watermarkFieldCopy: z3.boolean().default(true)
});
var ProductionBandsSchema = z3.object({
  green: z3.number().min(0).max(200),
  amber: z3.number().min(0).max(200)
}).refine((b) => b.green > b.amber, {
  message: "green threshold must be above amber",
  path: ["green"]
});
var CompanySchema = z3.object({
  id: Uuid,
  name: z3.string().min(1).max(120),
  logoUrl: z3.string().url().nullable(),
  standardDayHours: HoursString,
  cutoffTime: TimeOfDay,
  timezone: Timezone,
  /** Subset of UNITS (doc 17 units_enabled); at least one so the cost code unit picker is never empty. */
  unitsEnabled: z3.array(UnitSchema).min(1),
  weekStartDay: z3.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]).default("mon"),
  photoUploadOnCellular: z3.boolean().default(true),
  foremanCanCreateJob: z3.boolean().default(true),
  foremanCanAddCostCode: z3.boolean().default(false),
  foremanCanAddWorker: z3.boolean().default(true),
  reopenWindowHours: z3.number().int().min(0).max(168).default(48),
  productionBands: ProductionBandsSchema,
  clientDefaultEmails: z3.array(z3.string().email()).default([]),
  reportConfig: ReportConfigSchema,
  autoSendReports: z3.boolean().default(false),
  autoSendTime: TimeOfDay.default("18:30"),
  ownerSummaryEmail: z3.object({
    enabled: z3.boolean(),
    time: TimeOfDay,
    recipients: z3.array(z3.string().email())
  }).default({ enabled: false, time: "19:30", recipients: [] }),
  qboExportMode: z3.enum(["time_activity", "csv"]).default("time_activity"),
  /** doc 17 `support_access_allowed`: the owner can revoke Fuorix's read-only view of the company. */
  supportAccessAllowed: z3.boolean().default(true),
  syncVersion: z3.number().int().nonnegative()
});
var UserSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  name: z3.string().min(1).max(120),
  email: z3.string().email().nullable(),
  phone: z3.string().nullable(),
  role: Role,
  defaultHourlyCost: NonNegativeDecimalString,
  active: z3.boolean(),
  qboEmployeeId: z3.string().nullable(),
  /** Set when a foreman added this worker from the field and the office has not confirmed the role. */
  addedByUserId: Uuid.nullable().optional()
});
var JobSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  name: z3.string().min(1).max(160),
  clientName: z3.string().max(160).nullable(),
  siteAddress: z3.string().max(300).nullable(),
  status: JobStatus,
  startDate: DateOnly.nullable(),
  qboCustomerId: z3.string().nullable(),
  createdByForeman: z3.boolean(),
  createdBy: Uuid.nullable(),
  clientEmails: z3.array(z3.string().email()).default([])
});
var JobForemanSchema = z3.object({
  jobId: Uuid,
  userId: Uuid,
  companyId: Uuid,
  syncVersion: z3.number().int().nonnegative(),
  deletedAt: NullableIsoTimestamp.optional()
});
var JobCostCodeSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  jobId: Uuid,
  code: z3.string().min(1).max(40),
  description: z3.string().min(1).max(200),
  unit: UnitSchema,
  plannedTotalQty: NonNegativeDecimalString.nullable(),
  plannedQtyPerDay: NonNegativeDecimalString.nullable(),
  qboItemId: z3.string().nullable(),
  qboClassId: z3.string().nullable(),
  archived: z3.boolean(),
  sortOrder: z3.number().int()
});
var EquipmentSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  name: z3.string().min(1).max(120),
  type: z3.string().max(60).nullable(),
  unitNumber: z3.string().max(40).nullable(),
  defaultHourlyCost: NonNegativeDecimalString,
  active: z3.boolean(),
  /** Kept from doc 04; unused since Q-0 2/5 (the service item comes from the cost code). */
  qboItemId: z3.string().nullable(),
  /** QuickBooks Vendor standing in for the machine when `qbo_export_equipment` is on (Q-0 2/5). */
  qboVendorId: z3.string().nullable().default(null)
});
var DayEntrySchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  jobId: Uuid,
  foremanId: Uuid,
  entryDate: DateOnly,
  status: EntryStatus,
  weatherCondition: WeatherCondition.nullable(),
  temperatureF: z3.number().int().min(-80).max(150).nullable(),
  notes: z3.string().max(4e3).nullable(),
  finishedAt: NullableIsoTimestamp,
  approvedBy: Uuid.nullable(),
  approvedAt: NullableIsoTimestamp,
  version: z3.number().int().positive(),
  deviceId: z3.string().nullable()
});
var CrewLineSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  dayEntryId: Uuid,
  userId: Uuid,
  hours: HoursString,
  qboTimeActivityId: z3.string().nullable()
});
var EquipmentLineSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  dayEntryId: Uuid,
  equipmentId: Uuid,
  hours: HoursString,
  qboRef: z3.string().nullable()
});
var QuantityLineSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  dayEntryId: Uuid,
  jobCostCodeId: Uuid,
  quantity: NonNegativeDecimalString,
  qboRef: z3.string().nullable()
});
var EntryEventSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  dayEntryId: Uuid,
  type: EventType,
  durationMinutes: z3.number().int().min(0).max(24 * 60).nullable(),
  description: z3.string().max(1e3).nullable()
});
var PhotoSchema = SyncColumns.extend({
  id: Uuid,
  companyId: Uuid,
  dayEntryId: Uuid,
  storageKey: z3.string().nullable(),
  takenAt: IsoTimestamp,
  caption: z3.string().max(200).nullable(),
  width: z3.number().int().positive().nullable(),
  height: z3.number().int().positive().nullable(),
  bytes: z3.number().int().nonnegative().nullable(),
  uploadedAt: NullableIsoTimestamp
});
var EntryRevisionSchema = z3.object({
  id: Uuid,
  companyId: Uuid,
  dayEntryId: Uuid,
  version: z3.number().int().positive(),
  changedBy: Uuid,
  changedByName: z3.string().optional(),
  changedAt: IsoTimestamp,
  source: RevisionSource,
  reason: z3.string().nullable(),
  snapshot: z3.unknown()
});

// src/schemas/api/errors.ts
import { z as z4 } from "zod";
var ERROR_CODES = [
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "ENTRY_VERSION_STALE",
  "ENTRY_LOCKED",
  "ENTRY_EXPORTED",
  "OTP_INVALID",
  "OTP_RATE_LIMITED",
  "TOKEN_REUSED",
  "RATE_LIMITED",
  "PAYLOAD_TOO_LARGE",
  "QBO_NOT_CONNECTED",
  "NOT_IMPLEMENTED",
  "INTERNAL"
];
var ErrorCode = z4.enum(ERROR_CODES);
var HTTP_STATUS_FOR_CODE = {
  VALIDATION_FAILED: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  ENTRY_VERSION_STALE: 409,
  ENTRY_LOCKED: 409,
  ENTRY_EXPORTED: 409,
  OTP_INVALID: 400,
  OTP_RATE_LIMITED: 429,
  TOKEN_REUSED: 401,
  RATE_LIMITED: 429,
  PAYLOAD_TOO_LARGE: 413,
  QBO_NOT_CONNECTED: 409,
  NOT_IMPLEMENTED: 501,
  INTERNAL: 500
};
var ApiErrorSchema = z4.object({
  error: z4.object({
    code: ErrorCode,
    message: z4.string(),
    details: z4.array(z4.unknown()).optional()
  })
});

// src/schemas/api/auth.ts
import { z as z5 } from "zod";
var PasswordLoginRequest = z5.object({
  email: Email,
  password: z5.string().min(8).max(200)
});
var OtpRequest = z5.object({ email: Email });
var OtpVerifyRequest = z5.object({
  email: Email,
  code: z5.string().regex(/^\d{6}$/),
  deviceId: z5.string().min(8).max(80),
  deviceName: z5.string().max(120).optional()
});
var RefreshRequest = z5.object({ refreshToken: z5.string().min(20) });
var LogoutRequest = z5.object({ refreshToken: z5.string().min(20) });
var AuthUser = UserSchema.pick({
  id: true,
  companyId: true,
  name: true,
  email: true,
  phone: true,
  role: true
});
var SupportHandoffResponse = z5.object({
  user: AuthUser,
  expiresAt: z5.string().datetime({ offset: true })
});
var TokenResponse = z5.object({
  accessToken: z5.string(),
  refreshToken: z5.string(),
  user: AuthUser
});
var RefreshResponse = z5.object({ accessToken: z5.string(), refreshToken: z5.string() });
var MeResponse = z5.object({
  user: AuthUser,
  company: z5.object({
    id: Uuid,
    name: z5.string(),
    logoUrl: z5.string().url().nullable(),
    standardDayHours: HoursString,
    cutoffTime: TimeOfDay,
    timezone: z5.string(),
    unitsEnabled: z5.array(UnitSchema),
    foremanCanCreateJob: z5.boolean(),
    foremanCanAddCostCode: z5.boolean(),
    foremanCanAddWorker: z5.boolean(),
    reopenWindowHours: z5.number().int(),
    photoUploadOnCellular: z5.boolean()
  }),
  /** True for a session opened from the Fuorix support panel: every non-GET request answers 403 (doc 13 "Support panel"). */
  impersonating: z5.boolean().default(false),
  supportAdminName: z5.string().nullable().optional(),
  /** Where "Exit" sends a support session after logout (`SITEDAY_ADMIN_URL`); absent for normal sessions. */
  supportReturnUrl: z5.string().url().nullable().optional()
});
var JwtClaims = z5.object({
  sub: Uuid,
  cid: Uuid,
  role: z5.enum(["owner", "admin", "foreman"]),
  did: z5.string().optional(),
  /** Support impersonation (doc 13 "Support panel"): the support admin's id; the session is read-only. */
  imp: Uuid.optional(),
  iat: z5.number(),
  exp: z5.number()
});

// src/schemas/api/sync.ts
import { z as z6 } from "zod";
var SoftDeletable = { deletedAt: NullableIsoTimestamp.optional() };
var CrewLineDoc = z6.object({ id: Uuid, userId: Uuid, hours: HoursString, ...SoftDeletable });
var EquipmentLineDoc = z6.object({
  id: Uuid,
  equipmentId: Uuid,
  hours: HoursString,
  ...SoftDeletable
});
var QuantityLineDoc = z6.object({
  id: Uuid,
  jobCostCodeId: Uuid,
  quantity: NonNegativeDecimalString,
  ...SoftDeletable
});
var EventDoc = z6.object({
  id: Uuid,
  type: EventType,
  durationMinutes: z6.number().int().min(0).max(24 * 60).nullable(),
  description: z6.string().max(1e3).nullable(),
  ...SoftDeletable
});
var PhotoDoc = z6.object({
  id: Uuid,
  takenAt: IsoTimestamp,
  caption: z6.string().max(200).nullable(),
  width: z6.number().int().positive().nullable(),
  height: z6.number().int().positive().nullable(),
  bytes: z6.number().int().nonnegative().nullable(),
  ...SoftDeletable
});
var DayEntryDocument = z6.object({
  id: Uuid,
  jobId: Uuid,
  foremanId: Uuid,
  entryDate: DateOnly,
  status: EntryStatus,
  weatherCondition: WeatherCondition.nullable(),
  temperatureF: z6.number().int().min(-80).max(150).nullable(),
  notes: z6.string().max(4e3).nullable(),
  finishedAt: NullableIsoTimestamp,
  /** version the device believes is current */
  version: z6.number().int().positive(),
  /** version the device last pulled or acked; the server's conflict check key */
  baseVersion: z6.number().int().nonnegative(),
  crewLines: z6.array(CrewLineDoc),
  equipmentLines: z6.array(EquipmentLineDoc),
  quantityLines: z6.array(QuantityLineDoc),
  events: z6.array(EventDoc),
  photos: z6.array(PhotoDoc),
  /** Workers added from the field (doc 07 M3); server creates them if missing. */
  newWorkers: z6.array(z6.object({ id: Uuid, name: z6.string().min(1).max(120) })).optional(),
  /** Cost codes added from the field when foremanCanAddCostCode is on. */
  newCostCodes: z6.array(
    z6.object({
      id: Uuid,
      code: z6.string().min(1).max(40),
      description: z6.string().min(1).max(200),
      unit: JobCostCodeSchema.shape.unit
    })
  ).optional()
});
var JobDocument = z6.object({
  id: Uuid,
  name: z6.string().min(1).max(160),
  clientName: z6.string().max(160).nullable(),
  createdByForeman: z6.literal(true),
  startDate: DateOnly.nullable().optional()
});
var OutboxItem = z6.discriminatedUnion("entity", [
  z6.object({
    seq: z6.number().int().nonnegative(),
    entity: z6.literal("day_entry"),
    entityId: Uuid,
    op: z6.enum(["upsert", "delete"]),
    payload: DayEntryDocument,
    clientUpdatedAt: IsoTimestamp
  }),
  z6.object({
    seq: z6.number().int().nonnegative(),
    entity: z6.literal("job"),
    entityId: Uuid,
    op: z6.literal("upsert"),
    payload: JobDocument,
    clientUpdatedAt: IsoTimestamp
  })
]);
var SYNC_PUSH_MAX_ITEMS = 50;
var SYNC_PUSH_MAX_BYTES = 1024 * 1024;
var SyncPushRequest = z6.object({
  deviceId: z6.string().min(8).max(80),
  batch: z6.array(OutboxItem).max(SYNC_PUSH_MAX_ITEMS)
});
var SyncPushResult = z6.object({
  entityId: Uuid,
  status: z6.enum(["ok", "conflict", "rejected"]),
  syncVersion: z6.number().int().optional(),
  version: z6.number().int().optional(),
  error: z6.object({ code: z6.string(), message: z6.string() }).optional(),
  /** on conflict: the server's current document so the device can show it read-only */
  serverDocument: DayEntryDocument.optional()
});
var SyncPushResponse = z6.object({
  results: z6.array(SyncPushResult),
  serverTime: IsoTimestamp
});
var SyncPullQuery = z6.object({
  since: z6.coerce.number().int().nonnegative().default(0),
  deviceId: z6.string().min(8).max(80)
});
var SyncPullResponse = z6.object({
  company: CompanySchema.nullable(),
  users: z6.array(UserSchema),
  jobs: z6.array(JobSchema),
  jobForemen: z6.array(JobForemanSchema),
  jobCostCodes: z6.array(JobCostCodeSchema),
  equipment: z6.array(EquipmentSchema),
  dayEntries: z6.array(DayEntrySchema),
  crewLines: z6.array(CrewLineSchema),
  equipmentLines: z6.array(EquipmentLineSchema),
  quantityLines: z6.array(QuantityLineSchema),
  events: z6.array(EntryEventSchema),
  photos: z6.array(PhotoSchema),
  latestVersion: z6.number().int().nonnegative(),
  serverTime: IsoTimestamp
});
var RegisterDeviceRequest = z6.object({
  deviceId: z6.string().min(8).max(80),
  platform: z6.enum(["ios", "android", "other"]),
  appVersion: z6.string().max(40)
});
var REJECTED_BACKOFF_MINUTES = [1, 5, 30, 120, 720];
var REJECTED_MAX_ATTEMPTS = 5;
function coalesceOutbox(items) {
  const byEntity = /* @__PURE__ */ new Map();
  for (const item of [...items].sort((a, b) => a.seq - b.seq)) {
    const key = `${item.entity}:${item.entityId}`;
    const existing = byEntity.get(key);
    if (!existing) {
      byEntity.set(key, item);
    } else {
      byEntity.set(key, { ...item, seq: existing.seq });
    }
  }
  return [...byEntity.values()].sort((a, b) => a.seq - b.seq);
}
function validateFinishDay(doc, planPerDayByCostCode = {}) {
  const errors = [];
  const warnings = [];
  const live = (xs) => xs.filter((x) => !x.deletedAt);
  const crew = live(doc.crewLines).filter((l) => Number(l.hours) > 0);
  if (crew.length === 0) errors.push("Add at least one crew member with hours.");
  const quantities = live(doc.quantityLines).filter((q) => Number(q.quantity) > 0);
  const events = live(doc.events);
  if (quantities.length === 0 && events.length === 0) {
    errors.push("Log at least one quantity, or a delay or extra work.");
  }
  if (live(doc.photos).length === 0) warnings.push("No photos yet.");
  for (const l of live(doc.crewLines)) {
    if (Number(l.hours) > 14) warnings.push(`A crew member has ${l.hours} h, more than 14.`);
  }
  for (const q of quantities) {
    const plan = planPerDayByCostCode[q.jobCostCodeId];
    if (plan && Number(plan) > 0 && Number(q.quantity) > Number(plan) * 3) {
      warnings.push(`A quantity is more than 3x its plan per day. Double-check it.`);
    }
  }
  return { errors, warnings };
}

// src/schemas/api/setup.ts
import { z as z7 } from "zod";
var CompanyPatch = CompanySchema.omit({ id: true, logoUrl: true, syncVersion: true }).partial();
var LOGIN_ROLES = ["owner", "admin", "foreman"];
var UserFields = z7.object({
  name: z7.string().min(1).max(120),
  role: z7.enum(["owner", "admin", "foreman", "worker"]),
  email: Email.optional(),
  phone: Phone.optional(),
  defaultHourlyCost: NonNegativeDecimalString.default("0")
});
var requiresEmail = (v) => !v.role || !LOGIN_ROLES.includes(v.role) || Boolean(v.email);
var emailIssue = { message: "owners, admins and foremen need an email to sign in", path: ["email"] };
var UserCreate = UserFields.refine(requiresEmail, emailIssue);
var UserPatch = UserFields.partial().extend({ active: z7.boolean().optional() }).refine(requiresEmail, emailIssue);
var UsersQuery = z7.object({
  role: z7.enum(["owner", "admin", "foreman", "worker"]).optional(),
  active: z7.enum(["true", "false"]).transform((v) => v === "true").optional()
});
var UserResponse = UserSchema;
var JobCreate = z7.object({
  name: z7.string().min(1).max(160),
  clientName: z7.string().max(160).nullable().optional(),
  siteAddress: z7.string().max(300).nullable().optional(),
  status: JobStatus.default("active"),
  startDate: DateOnly.nullable().optional(),
  qboCustomerId: z7.string().nullable().optional(),
  foremanIds: z7.array(Uuid).default([]),
  clientEmails: z7.array(Email).default([])
});
var JobPatch = JobCreate.omit({ foremanIds: true }).partial();
var JobsQuery = z7.object({ status: JobStatus.optional(), search: z7.string().optional() });
var SetForemenRequest = z7.object({ userIds: z7.array(Uuid) });
var ReconcileRequest = z7.object({ intoJobId: Uuid });
var JobResponse = JobSchema.extend({
  foremen: z7.array(z7.object({ id: Uuid, name: z7.string() })),
  costCodes: z7.array(JobCostCodeSchema).optional()
});
var CostCodeCreate = z7.object({
  code: z7.string().min(1).max(40),
  description: z7.string().min(1).max(200),
  unit: UnitSchema,
  plannedTotalQty: NonNegativeDecimalString.nullable().optional(),
  plannedQtyPerDay: NonNegativeDecimalString.nullable().optional(),
  qboItemId: z7.string().nullable().optional(),
  qboClassId: z7.string().nullable().optional()
});
var CostCodePatch = CostCodeCreate.partial();
var CostCodeOrderRequest = z7.object({ ids: z7.array(Uuid).min(1) });
var EquipmentCreate = z7.object({
  name: z7.string().min(1).max(120),
  type: z7.string().max(60).nullable().optional(),
  unitNumber: z7.string().max(40).nullable().optional(),
  defaultHourlyCost: NonNegativeDecimalString.default("0"),
  qboItemId: z7.string().nullable().optional()
});
var EquipmentPatch = EquipmentCreate.partial().extend({ active: z7.boolean().optional() });
var EquipmentQuery = z7.object({ active: z7.enum(["true", "false"]).transform((v) => v === "true").optional() });
var EquipmentResponse = EquipmentSchema;

// src/schemas/api/entries.ts
import { z as z8 } from "zod";
var EntriesQuery = z8.object({
  jobId: Uuid.optional(),
  foremanId: Uuid.optional(),
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  status: EntryStatus.optional(),
  limit: z8.coerce.number().int().min(1).max(200).default(50),
  cursor: z8.string().optional()
});
var EntryListItem = DayEntrySchema.extend({
  jobName: z8.string(),
  foremanName: z8.string(),
  laborHours: z8.string(),
  machineHours: z8.string(),
  photoCount: z8.number().int(),
  quantitySummary: z8.array(z8.object({ code: z8.string(), unit: z8.string(), quantity: z8.string() }))
});
var EntryFull = DayEntrySchema.extend({
  jobName: z8.string(),
  clientName: z8.string().nullable(),
  foremanName: z8.string(),
  crewLines: z8.array(CrewLineSchema.extend({ userName: z8.string() })),
  equipmentLines: z8.array(
    EquipmentLineSchema.extend({ equipmentName: z8.string(), unitNumber: z8.string().nullable() })
  ),
  quantityLines: z8.array(
    QuantityLineSchema.extend({
      code: z8.string(),
      description: z8.string(),
      unit: z8.string(),
      plannedQtyPerDay: z8.string().nullable(),
      pct: z8.number().nullable(),
      band: z8.enum(["green", "amber", "red", "none"]),
      toDate: z8.string().optional(),
      plannedTotal: z8.string().nullable().optional()
    })
  ),
  events: z8.array(EntryEventSchema),
  photos: z8.array(PhotoSchema.extend({ url: z8.string().nullable() })),
  revisions: z8.array(EntryRevisionSchema),
  exportedToQbo: z8.boolean(),
  /**
   * An office correction after export is waiting for the next `qbo.export` run
   * (a TimeActivity line is behind the entry version, was added, removed or
   * zeroed). Cleared by the run; false when nothing was exported yet.
   */
  qboCorrectionPending: z8.boolean().default(false)
});
var EntryPatchRequest = z8.object({
  reason: z8.string().min(5).max(500),
  baseVersion: z8.number().int().positive(),
  document: DayEntryDocument.omit({ version: true, baseVersion: true, newWorkers: true, newCostCodes: true })
});
var ApproveWeekRequest = z8.object({ jobId: Uuid, weekStart: DateOnly });
var ResolveConflictRequest = z8.discriminatedUnion("choose", [
  z8.object({ choose: z8.literal("office") }),
  z8.object({ choose: z8.literal("field") }),
  z8.object({
    choose: z8.literal("merged"),
    merged: DayEntryDocument.omit({ version: true, baseVersion: true, newWorkers: true, newCostCodes: true })
  })
]);
var EmailReportRequest = z8.object({ to: z8.array(z8.string().email()).min(1).max(20).optional() });
var ReportEmailStatus = z8.object({
  id: Uuid,
  sentTo: z8.array(z8.string()),
  sentAt: IsoTimestamp.nullable(),
  status: z8.enum(["queued", "sent", "failed"]),
  error: z8.string().nullable(),
  /** `manual` = POST /entries/:id/email; `auto` = company auto-send at autoSendTime. */
  kind: z8.enum(["manual", "auto"]).default("manual"),
  createdAt: IsoTimestamp.optional()
});
var WeeklyReportQuery = z8.object({
  jobId: Uuid,
  weekStart: DateOnly,
  format: z8.enum(["pdf", "csv"]).default("pdf")
});

// src/schemas/api/dashboard.ts
import { z as z9 } from "zod";
var BandSchema = z9.enum(["green", "amber", "red", "none"]);
var TodayQuantity = z9.object({
  costCodeId: Uuid,
  code: z9.string(),
  description: z9.string(),
  unit: z9.string(),
  actual: z9.string().nullable(),
  plan: z9.string().nullable(),
  pct: z9.number().nullable(),
  band: BandSchema
});
var TodayForemanRow = z9.object({
  foremanId: Uuid,
  foremanName: z9.string(),
  entryId: Uuid.nullable(),
  entryStatus: EntryStatus.nullable(),
  version: z9.number().int().nullable(),
  submittedAt: IsoTimestamp.nullable(),
  missing: z9.boolean(),
  laborHours: z9.string(),
  machineHours: z9.string(),
  photoCount: z9.number().int(),
  photosPending: z9.number().int(),
  quantities: z9.array(TodayQuantity)
});
var TodayJobRow = z9.object({
  jobId: Uuid,
  jobName: z9.string(),
  clientName: z9.string().nullable(),
  foremen: z9.array(TodayForemanRow),
  totals: z9.object({ laborHours: z9.string(), machineHours: z9.string(), photoCount: z9.number().int() })
});
var DashboardTodayResponse = z9.object({
  date: DateOnly,
  cutoffTime: z9.string(),
  pastCutoff: z9.boolean(),
  summary: z9.object({
    foremanDays: z9.number().int(),
    submitted: z9.number().int(),
    missing: z9.number().int(),
    codesBehind: z9.number().int(),
    codesRed: z9.number().int(),
    codesAmber: z9.number().int(),
    laborHours: z9.string(),
    machineHours: z9.string(),
    photoCount: z9.number().int(),
    photosPending: z9.number().int()
  }),
  jobs: z9.array(TodayJobRow),
  generatedAt: IsoTimestamp
});
var ToDateCostCode = z9.object({
  costCodeId: Uuid,
  code: z9.string(),
  description: z9.string(),
  unit: z9.string(),
  archived: z9.boolean(),
  actualTotal: z9.string(),
  plannedTotal: z9.string().nullable(),
  pctComplete: z9.number().nullable(),
  daysLogged: z9.number().int(),
  avgPerDay: z9.string().nullable(),
  planPerDay: z9.string().nullable(),
  pacePct: z9.number().nullable(),
  paceBand: BandSchema
});
var ToDateSeriesPoint = z9.object({ date: DateOnly, actual: z9.string() });
var JobToDateResponse = z9.object({
  jobId: Uuid,
  daysWorked: z9.number().int(),
  firstDate: DateOnly.nullable(),
  lastDate: DateOnly.nullable(),
  laborHours: z9.string(),
  machineHours: z9.string(),
  laborCostEstimate: z9.string(),
  machineCostEstimate: z9.string(),
  costCodes: z9.array(ToDateCostCode),
  series: z9.record(Uuid, z9.array(ToDateSeriesPoint))
});
var DashboardFlagsResponse = z9.object({
  date: DateOnly,
  redJobs: z9.array(z9.object({ jobId: Uuid, jobName: z9.string(), codes: z9.array(z9.string()) })),
  missing: z9.array(z9.object({ jobId: Uuid, jobName: z9.string(), foremanId: Uuid, foremanName: z9.string() }))
});

// src/schemas/api/photos.ts
import { z as z10 } from "zod";
var PHOTO_MAX_BYTES = 5 * 1024 * 1024;
var PHOTO_CONTENT_TYPES = ["image/jpeg", "image/png"];
var PhotoUploadUrlRequest = z10.object({
  photoId: Uuid,
  dayEntryId: Uuid,
  contentType: z10.enum(PHOTO_CONTENT_TYPES),
  bytes: z10.number().int().positive().max(PHOTO_MAX_BYTES)
});
var PhotoUploadUrlResponse = z10.object({
  url: z10.string().url(),
  key: z10.string(),
  headers: z10.record(z10.string())
});
var PhotoCompleteRequest = z10.object({
  key: z10.string().min(1),
  width: z10.number().int().positive(),
  height: z10.number().int().positive(),
  bytes: z10.number().int().positive().max(PHOTO_MAX_BYTES)
});
var PhotoUrlResponse = z10.object({ url: z10.string().url() });

// src/schemas/api/company.ts
import { z as z11 } from "zod";
var COMPANY_LOGO_MAX_BYTES = 2 * 1024 * 1024;
var COMPANY_LOGO_CONTENT_TYPES = ["image/png", "image/jpeg"];
var COMPANY_LOGO_MAX_EDGE_PX = 600;
var COMPANY_LOGO_FIELD = "logo";
var CompanyLogoResponse = z11.object({ logoUrl: z11.string().url() });

// src/schemas/api/exports.ts
import { z as z12 } from "zod";
var EXPORT_LINK_TTL_DAYS = 7;
var EXPORT_CSV_FILES = [
  "companies.csv",
  "users.csv",
  "jobs.csv",
  "job_cost_codes.csv",
  "equipment.csv",
  "day_entries.csv",
  "crew_lines.csv",
  "equipment_lines.csv",
  "quantity_lines.csv",
  "events.csv",
  "photos.csv",
  "entry_revisions.csv",
  "sync_logs.csv"
];
var ExportStatusValue = z12.enum([
  "queued",
  "running",
  "ready",
  "failed"
]);
var ExportCounts = z12.record(
  z12.string(),
  z12.number().int().nonnegative()
);
var ExportCreateResponse = z12.object({ exportId: Uuid });
var ExportStatus = z12.object({
  id: Uuid,
  status: ExportStatusValue,
  requestedAt: IsoTimestamp,
  finishedAt: IsoTimestamp.optional(),
  /** Signed link, present only while `status` is `ready` and the link has not expired. */
  downloadUrl: z12.string().url().optional(),
  expiresAt: IsoTimestamp.optional(),
  error: z12.string().optional(),
  counts: ExportCounts.optional()
});

// src/schemas/api/quickbooks.ts
import { z as z13 } from "zod";
var QboExportMode = z13.enum(["time_activity", "csv"]);
var QboLaborCostCodeRule = z13.enum(["single", "proportional"]);
var QboBillableStatus = z13.enum(["NotBillable", "Billable"]);
var QboNameOf = z13.enum(["Employee", "Vendor"]);
var QboEnvironment = z13.enum(["sandbox", "production"]);
var QboStatus = z13.object({
  connected: z13.boolean(),
  /** Client id + secret present in the environment; when false Connect cannot start. */
  configured: z13.boolean(),
  environment: QboEnvironment,
  realmId: z13.string().nullable(),
  companyName: z13.string().nullable(),
  connectedAt: IsoTimestamp.nullable(),
  /** Why the connection was dropped (e.g. `invalid_grant`), null while connected or never connected. */
  disconnectedReason: z13.string().nullable(),
  exportMode: QboExportMode,
  laborCostCodeRule: QboLaborCostCodeRule,
  exportEquipment: z13.boolean(),
  billableStatus: QboBillableStatus,
  /** Rolling 100-day refresh-token expiry (Q-0 5/5); null when not connected. */
  refreshExpiresAt: IsoTimestamp.nullable(),
  /** Under 30 days of refresh-token life left: W10 shows a "reconnect soon" banner. */
  reconnectSoon: z13.boolean(),
  lastImport: IsoTimestamp.nullable(),
  lastImportSummary: z13.string().nullable(),
  lastExport: IsoTimestamp.nullable(),
  lastExportSummary: z13.string().nullable()
});
var QboSettingsPatch = z13.object({
  exportMode: QboExportMode,
  laborCostCodeRule: QboLaborCostCodeRule,
  exportEquipment: z13.boolean(),
  billableStatus: z13.literal("NotBillable")
}).partial();
var QboCandidate = z13.object({
  id: z13.string(),
  name: z13.string(),
  /** ParentRef for sub-customers / sub-items; null at the top level. */
  parentId: z13.string().nullable(),
  /** "Harmon Builders:Oak Ridge Ph2" — what QuickBooks shows. */
  fullyQualifiedName: z13.string(),
  active: z13.boolean(),
  /** Customers only: a QuickBooks Project (Customer with IsProject) → the export adds ProjectRef (Q-0 1/5). */
  isProject: z13.boolean().default(false),
  /** Customers only: classic sub-customer (Job = true). */
  job: z13.boolean().default(false)
});
var QboItemSuggestion = z13.object({
  code: z13.string(),
  qboItemId: z13.string(),
  itemName: z13.string()
});
var QboCandidates = z13.object({
  customers: z13.array(QboCandidate),
  items: z13.array(QboCandidate),
  classes: z13.array(QboCandidate),
  employees: z13.array(QboCandidate),
  /** Equipment maps to a Vendor (Q-0 2/5); only used when `exportEquipment` is on. */
  vendors: z13.array(QboCandidate),
  /** Class tracking per transaction line is on in the QuickBooks company (else `classes` is empty). */
  classTracking: z13.boolean(),
  suggestions: z13.array(QboItemSuggestion),
  importedAt: IsoTimestamp.nullable(),
  syncLogId: Uuid.nullable()
});
var QboMappingsRequest = z13.object({
  jobs: z13.array(z13.object({ jobId: Uuid, qboCustomerId: z13.string().min(1).nullable() })).default([]),
  costCodes: z13.array(
    z13.object({
      id: Uuid,
      qboItemId: z13.string().min(1).nullable().optional(),
      qboClassId: z13.string().min(1).nullable().optional()
    })
  ).default([]),
  /** W10 "apply to all jobs with same code": each cost-code mapping is copied to every job's code with the same `code`. */
  applyToSameCode: z13.boolean().default(false),
  users: z13.array(z13.object({ userId: Uuid, qboEmployeeId: z13.string().min(1).nullable() })).default([]),
  /** Machine → QuickBooks Vendor (Q-0 2/5: the service item comes from the cost code, not the machine). */
  equipment: z13.array(z13.object({ id: Uuid, qboVendorId: z13.string().min(1).nullable() })).default([])
});
var QboMappingsResponse = z13.object({
  updated: z13.object({
    jobs: z13.number().int(),
    costCodes: z13.number().int(),
    users: z13.number().int(),
    equipment: z13.number().int()
  })
});
var QboExportRequest = z13.object({
  jobId: Uuid.optional(),
  /** Monday (or the company's week start) of the week to export; omitted = everything approved and unexported. */
  weekStart: DateOnly.optional()
});
var QboLineStatus = z13.enum(["mapped", "UNMAPPED_EMPLOYEE", "UNMAPPED_VENDOR", "UNMAPPED_CUSTOMER", "UNMAPPED_ITEM"]);
var QboExportPreviewRow = z13.object({
  entryId: Uuid,
  lineId: Uuid,
  /** crew → NameOf Employee; equipment (when exportEquipment) → NameOf Vendor. */
  lineKind: z13.enum(["crew", "equipment"]),
  nameOf: QboNameOf,
  date: DateOnly,
  jobName: z13.string(),
  foreman: z13.string(),
  /** Person name, or the machine name for a vendor row. */
  employee: z13.string(),
  customer: z13.string().nullable(),
  /** Set when the mapped customer is a QuickBooks Project (ProjectRef is sent too). */
  project: z13.boolean(),
  item: z13.string().nullable(),
  class: z13.string().nullable(),
  /** Decimal string of hours for this row (minutes / 60). */
  hours: z13.string(),
  minutes: z13.number().int().nonnegative(),
  status: QboLineStatus,
  /** True when this row updates an already exported TimeActivity (entry version moved on). */
  update: z13.boolean()
});
var QboExportPreviewEntry = z13.object({
  entryId: Uuid,
  label: z13.string(),
  date: DateOnly,
  jobName: z13.string(),
  foreman: z13.string(),
  status: z13.string(),
  version: z13.number().int(),
  timeActivities: z13.number().int(),
  hours: z13.string(),
  unmapped: z13.number().int()
});
var QboExportPreview = z13.object({
  mode: QboExportMode,
  laborCostCodeRule: QboLaborCostCodeRule,
  billableStatus: QboBillableStatus,
  filter: QboExportRequest,
  rows: z13.array(QboExportPreviewRow),
  entries: z13.array(QboExportPreviewEntry),
  counts: z13.object({
    entries: z13.number().int(),
    timeActivities: z13.number().int(),
    updates: z13.number().int(),
    mapped: z13.number().int(),
    unmapped: z13.number().int(),
    unmappedEmployee: z13.number().int(),
    unmappedVendor: z13.number().int(),
    unmappedCustomer: z13.number().int(),
    unmappedItem: z13.number().int(),
    totalHours: z13.string()
  }),
  /**
   * What only reaches QuickBooks as CSV (Q-0 3/5: quantities always; equipment unless `exportEquipment`).
   * The second CSV is written in both export modes whenever it has lines.
   */
  csvOnly: z13.object({
    equipmentLines: z13.number().int(),
    equipmentHours: z13.string(),
    quantityLines: z13.number().int(),
    /** True when this run writes the quantities/equipment CSV. */
    included: z13.boolean()
  })
});
var QboSyncLogKind = z13.enum(["import", "export"]);
var QboSyncLogStatus = z13.enum(["running", "ok", "partial", "failed"]);
var QboSyncFailure = z13.object({
  id: z13.string(),
  lineId: Uuid.nullable(),
  entryId: Uuid.nullable(),
  label: z13.string(),
  /** `UNMAPPED_*`, `INTUIT_FAULT`, `TOKEN`, `HTTP` … */
  reason: z13.string(),
  message: z13.string()
});
var QboSyncDownload = z13.object({
  name: z13.string(),
  url: z13.string(),
  expiresAt: IsoTimestamp
});
var QboSyncLog = z13.object({
  id: Uuid,
  kind: QboSyncLogKind,
  status: QboSyncLogStatus,
  startedAt: IsoTimestamp,
  finishedAt: IsoTimestamp.nullable(),
  sent: z13.number().int(),
  failed: z13.number().int(),
  /** Lines skipped for missing mappings (not counted as failed). */
  skipped: z13.number().int(),
  summary: z13.string().nullable(),
  failures: z13.array(QboSyncFailure),
  /** Signed links to the CSV files of a csv-mode run (or the companion quantities/equipment CSV). */
  downloads: z13.array(QboSyncDownload),
  /** Export runs: the filter that was used; import runs: null. */
  filter: QboExportRequest.nullable(),
  /** True when a later run retried this run's failures. */
  retriedBySyncLogId: Uuid.nullable()
});
var QboSyncLogsResponse = z13.object({ items: z13.array(QboSyncLog) });
var QboRetryRequest = z13.object({
  lineIds: z13.array(Uuid).optional()
});
var QboQueuedResponse = z13.object({ syncLogId: Uuid });

// src/schemas/api/support.ts
import { z as z14 } from "zod";
var SupportLoginRequest = z14.object({
  email: Email,
  password: z14.string().min(8).max(200)
});
var SupportAdmin = z14.object({
  id: Uuid,
  email: Email,
  name: z14.string().min(1).max(120),
  lastSignInAt: IsoTimestamp.nullable()
});
var SupportLoginResponse = z14.object({
  admin: SupportAdmin,
  accessToken: z14.string(),
  expiresAt: IsoTimestamp
});
var SupportAdoption = z14.enum(["active", "quiet", "alerted", "new"]);
var DateOnlyString = z14.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var SupportCompanyRow = z14.object({
  id: Uuid,
  name: z14.string(),
  timezone: z14.string(),
  createdAt: IsoTimestamp,
  plan: z14.string(),
  supportAccessAllowed: z14.boolean(),
  counts: z14.object({
    owners: z14.number().int().nonnegative(),
    admins: z14.number().int().nonnegative(),
    foremen: z14.number().int().nonnegative(),
    workers: z14.number().int().nonnegative(),
    activeJobs: z14.number().int().nonnegative()
  }),
  /** Submitted entries (finished / needs_review / approved / synced) by entry_date, company-local today included. */
  entriesLast7: z14.number().int().nonnegative(),
  entriesLast30: z14.number().int().nonnegative(),
  lastEntryDate: DateOnlyString.nullable(),
  adoption: SupportAdoption,
  qboConnected: z14.boolean()
});
var SupportCompanyList = z14.object({ items: z14.array(SupportCompanyRow) });
var SupportCompanyUser = z14.object({
  id: Uuid,
  name: z14.string(),
  role: Role,
  email: z14.string().nullable(),
  active: z14.boolean(),
  /** Newest refresh token issued to the user; null when they never signed in. */
  lastSignInAt: IsoTimestamp.nullable().optional()
});
var SupportCompanyJob = z14.object({
  id: Uuid,
  name: z14.string(),
  status: JobStatus,
  foremanCount: z14.number().int().nonnegative(),
  lastEntryDate: DateOnlyString.nullable()
});
var SupportSyncLog = z14.object({
  id: Uuid,
  kind: z14.string(),
  status: z14.string(),
  startedAt: IsoTimestamp,
  finishedAt: IsoTimestamp.nullable(),
  recordsSent: z14.number().int().nonnegative(),
  recordsFailed: z14.number().int().nonnegative()
});
var SupportAuditEvent = z14.object({
  id: Uuid,
  at: IsoTimestamp,
  entity: z14.string(),
  entityId: z14.string().nullable(),
  action: z14.string(),
  userId: Uuid.nullable(),
  /** Resolved from `users` for tenant actors, "Fuorix support (name)" for support rows, null for the CLI. */
  actorName: z14.string().nullable()
});
var SupportCompanyDetail = SupportCompanyRow.extend({
  users: z14.array(SupportCompanyUser),
  jobs: z14.array(SupportCompanyJob),
  recentSyncLogs: z14.array(SupportSyncLog),
  recentAuditEvents: z14.array(SupportAuditEvent)
});
var SupportCreateCompanyRequest = z14.object({
  name: z14.string().trim().min(1).max(120),
  timezone: Timezone,
  standardDayHours: z14.number().min(1).max(24).default(10),
  cutoffTime: TimeOfDay.default("19:00"),
  ownerName: z14.string().trim().min(1).max(120),
  ownerEmail: Email
});
var SupportCreateCompanyResponse = z14.object({
  companyId: Uuid,
  ownerId: Uuid,
  /** Shown once; the API never stores or returns it again. */
  temporaryPassword: z14.string()
});
var SupportCompanyPatch = z14.object({
  supportAccessAllowed: z14.boolean(),
  plan: z14.string().trim().min(1).max(40)
}).partial().strict();
var SupportImpersonateResponse = z14.object({
  /** `${SITEDAY_WEB_URL}/support-handoff?code=…` — open in a new tab; valid for 60 s, single use. */
  handoffUrl: z14.string().url(),
  expiresAt: IsoTimestamp
});
var SupportHandoffRequest = z14.object({ code: z14.string().min(20).max(200) });
var SupportJwtClaims = z14.object({
  sub: Uuid,
  role: z14.literal("support"),
  iat: z14.number(),
  exp: z14.number()
});

// src/report-template/types.ts
import { z as z15 } from "zod";
var BandName = z15.enum(["green", "amber", "red", "none"]);
var ReportCompany = z15.object({
  name: z15.string(),
  logoUrl: z15.string().nullish(),
  footerText: z15.string().nullish()
});
var ReportJob = z15.object({
  name: z15.string(),
  clientName: z15.string().nullish(),
  siteAddress: z15.string().nullish()
});
var DailyReportQuantity = z15.object({
  code: z15.string(),
  description: z15.string(),
  unit: z15.string(),
  quantity: DecimalString,
  planPerDay: DecimalString.nullish(),
  /** Percent of plan/day, whole number; null/absent when there is no plan. */
  pct: z15.number().nullish(),
  /** Band for `pct` under the company's production bands; computed by the caller (shared `productionFor`). */
  band: BandName.nullish(),
  toDate: DecimalString.nullish(),
  plannedTotal: DecimalString.nullish()
});
var DailyReportEvent = z15.object({
  type: EventType,
  label: z15.string(),
  durationMinutes: z15.number().int().nonnegative().nullish(),
  description: z15.string().nullish()
});
var DailyReportPhoto = z15.object({
  url: z15.string(),
  takenAt: IsoTimestamp,
  caption: z15.string().nullish()
});
var DailyReportData = z15.object({
  company: ReportCompany,
  job: ReportJob,
  entry: z15.object({
    date: DateOnly,
    foremanName: z15.string(),
    status: EntryStatus,
    version: z15.number().int().positive(),
    weatherCondition: WeatherCondition.nullish(),
    temperatureF: z15.number().int().nullish(),
    notes: z15.string().nullish(),
    finishedAt: IsoTimestamp.nullish(),
    approvedAt: IsoTimestamp.nullish()
  }),
  crew: z15.array(z15.object({ name: z15.string(), hours: HoursString })),
  crewTotalHours: DecimalString,
  equipment: z15.array(z15.object({ name: z15.string(), unitNumber: z15.string().nullish(), hours: HoursString })),
  equipmentTotalHours: DecimalString,
  quantities: z15.array(DailyReportQuantity),
  events: z15.array(DailyReportEvent),
  photos: z15.array(DailyReportPhoto),
  /** Rendered only when `config.showCostEstimates` is on (doc 09 "Optional"). */
  costEstimates: z15.object({ labor: DecimalString, machine: DecimalString }).nullish(),
  /** "Field copy — not yet reviewed" (device copy of a finished-but-unapproved entry). Server PDFs never set it. */
  watermark: z15.boolean().default(false),
  /** IANA zone used to print clock times (finishedAt, photo timestamps). */
  timezone: z15.string().default("America/Chicago"),
  generatedAt: IsoTimestamp,
  reportId: z15.string()
});
var WeeklyReportEntry = z15.object({
  id: z15.string(),
  date: DateOnly,
  foremanName: z15.string(),
  status: EntryStatus,
  version: z15.number().int().positive(),
  notes: z15.string().nullish(),
  quantities: z15.array(z15.object({ code: z15.string(), description: z15.string(), unit: z15.string(), quantity: DecimalString })),
  labor: z15.array(z15.object({ name: z15.string(), hours: HoursString })),
  equipment: z15.array(z15.object({ name: z15.string(), unitNumber: z15.string().nullish(), hours: HoursString })),
  events: z15.array(DailyReportEvent),
  photoCount: z15.number().int().nonnegative()
});
var WeeklyReportData = z15.object({
  company: ReportCompany,
  job: ReportJob,
  /** First day of the week (company `weekStartDay`); `days` are the seven dates from it. */
  weekStart: DateOnly,
  days: z15.array(DateOnly).length(7),
  /** Cost codes of the job in office sort order; codes with no quantity this week still get a row. */
  costCodes: z15.array(
    z15.object({
      code: z15.string(),
      description: z15.string(),
      unit: z15.string(),
      plannedTotal: DecimalString.nullish(),
      /** Job to-date through the end of the week (for the last column), when known. */
      toDate: DecimalString.nullish()
    })
  ),
  entries: z15.array(WeeklyReportEntry),
  timezone: z15.string().default("America/Chicago"),
  generatedAt: IsoTimestamp,
  reportId: z15.string()
});

// src/report-template/html.ts
function escapeHtml(s) {
  if (s === null || s === void 0) return "";
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function escapeAttr(s) {
  if (!s) return "";
  const trimmed = String(s).trim();
  if (/^javascript:/i.test(trimmed)) return "";
  return escapeHtml(trimmed);
}
function fmtQty(value) {
  return formatQuantity(value);
}
function fmtHours(value) {
  return formatHours(value);
}
var WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
var WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
var MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function parts(dateOnly) {
  const [ys = "1970", ms = "01", ds = "01"] = dateOnly.split("-");
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return { y, m, d, weekday };
}
function fmtLongDate(dateOnly) {
  const p = parts(dateOnly);
  return `${WEEKDAYS[p.weekday]}, ${MONTHS[p.m - 1]} ${p.d}, ${p.y}`;
}
function fmtShortDate(dateOnly, withYear = false) {
  const p = parts(dateOnly);
  return `${WEEKDAYS_SHORT[p.weekday]}, ${MONTHS_SHORT[p.m - 1]} ${p.d}${withYear ? `, ${p.y}` : ""}`;
}
function fmtDayHead(dateOnly) {
  const p = parts(dateOnly);
  return `${WEEKDAYS_SHORT[p.weekday]} ${p.m}/${p.d}`;
}
function fmtTime(iso, timezone) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: timezone }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "UTC" }).format(d);
  }
}
function fmtDateTime(iso, timezone) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone
    }).format(d);
  } catch {
    return d.toISOString();
  }
}
function statusLabel(status) {
  switch (status) {
    case "approved":
    case "synced":
      return "Approved by office";
    case "needs_review":
      return "Needs office review";
    case "finished":
      return "Finished by foreman";
    default:
      return "Draft";
  }
}
function statusShort(status) {
  switch (status) {
    case "approved":
    case "synced":
      return "Approved";
    case "needs_review":
      return "Review";
    case "finished":
      return "Finished";
    case "draft":
      return "Draft";
    default:
      return "\u2014";
  }
}
function durationLabel(minutes) {
  if (minutes === null || minutes === void 0) return "";
  if (minutes < 60) return `${minutes} min`;
  const h = minutes / 60;
  const text = Number.isInteger(h) ? String(h) : h.toFixed(1).replace(/\.0$/, "");
  return `${text} h`;
}
var WEATHER_LABELS = {
  clear: "Clear",
  cloudy: "Cloudy",
  rain: "Rain",
  snow: "Snow",
  wind: "Wind"
};
function weatherLabel(condition, temperatureF) {
  const bits = [];
  if (condition) bits.push(WEATHER_LABELS[condition] ?? condition);
  if (temperatureF !== null && temperatureF !== void 0) bits.push(`${temperatureF}\xB0F`);
  return bits.join(" \xB7 ");
}
var BAND_CLASS = { green: "pg", amber: "pa", red: "pb" };
function cssString(s) {
  return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/</g, "\\3c ").replace(/>/g, "\\3e ").replace(/\r?\n/g, " ");
}
function baseCss(opts = {}) {
  const size = opts.landscape ? "Letter landscape" : "Letter";
  const box = "font-family: Helvetica, Arial, sans-serif; font-size: 8px; color: #777; vertical-align: middle;";
  const header = opts.header ? `
  @top-right { content: "${cssString(opts.header)}"; ${box} }` : "";
  const left = opts.footer?.left ? `
  @bottom-left { content: "${cssString(opts.footer.left)}"; ${box} }` : "";
  const right = opts.footer?.right ? `
  @bottom-right { content: "${cssString(opts.footer.right)}"; ${box} }` : "";
  return `
@page { size: ${size}; margin: 0.5in; margin-top: 0.6in; margin-bottom: 0.6in;${header}${left}
  @bottom-center { content: "Generated by SiteDay \xB7 Page " counter(page) " of " counter(pages); ${box} }${right} }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body { background: #fff; color: #111; font-family: Helvetica, Arial, sans-serif; font-size: 11px; line-height: 1.35;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.page { padding-bottom: 8px; }
.band { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 3px solid #E0762B; padding-bottom: 10px; margin-bottom: 14px; }
.logo { display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 16px; }
.logo img { height: 36px; max-width: 160px; object-fit: contain; display: block; }
.logo i { width: 36px; height: 36px; background: #E0762B; border-radius: 6px; display: inline-block; position: relative; flex: none; }
.logo i::after { content: ""; position: absolute; left: 8px; right: 8px; bottom: 8px; height: 6px; background: #111; border-radius: 2px; }
.logo .sub { font-weight: 400; font-size: 10px; color: #666; }
.rt { text-align: right; }
.rt h1 { margin: 0; font-size: 18px; font-weight: 700; }
.rt .co { color: #555; }
.wm { display: inline-block; margin-top: 6px; border: 2px solid #C2372B; color: #C2372B; font-weight: 700; font-size: 10px; letter-spacing: .06em; text-transform: uppercase; padding: 3px 8px; transform: rotate(-2deg); opacity: .85; }
.meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px 16px; margin: 0 0 14px; font-size: 11px; }
.meta div span { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: .08em; color: #777; }
.meta div b { font-size: 12px; color: #111; }
h2 { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; margin: 12px 0 6px; color: #333; border-bottom: 1px solid #DDD; padding-bottom: 3px; page-break-after: avoid; }
table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; }
tfoot { display: table-footer-group; }
tr { page-break-inside: avoid; }
th { text-align: left; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #666; padding: 4px 6px; border-bottom: 1px solid #999; }
td { padding: 4px 6px; border-bottom: 1px solid #E5E5E5; font-variant-numeric: tabular-nums; vertical-align: top; }
.r { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.c { text-align: center; }
.tot td { font-weight: 700; border-top: 1px solid #999; border-bottom: 0; }
.pg { color: #1F7A47; font-weight: 700; }
.pa { color: #946200; font-weight: 700; }
.pb { color: #C2372B; font-weight: 700; }
.muted { color: #777; }
.cols { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.photos { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.photos.small { grid-template-columns: repeat(3, 1fr); }
.photos figure { margin: 0; page-break-inside: avoid; }
.photos img { display: block; max-width: 100%; width: 100%; height: auto; max-height: 1200px; object-fit: cover; background: #EEE; }
.photos figcaption { font-size: 9px; color: #555; margin-top: 3px; }
.break { page-break-before: always; }
.pnotes { white-space: pre-line; margin: 0; }
.cost { margin-top: 8px; font-size: 10px; color: #555; }
.status-row td { font-size: 8px; text-transform: uppercase; letter-spacing: .06em; color: #777; padding-top: 2px; padding-bottom: 2px; border-bottom: 1px solid #999; }
.foot { display: flex; justify-content: space-between; font-size: 9px; color: #777; border-top: 1px solid #DDD; padding-top: 6px; margin-top: 24px; }
@media print { .foot { display: none; } }
@media screen { body { padding: 0.5in; } }
`;
}
function renderBand(input) {
  const logo = input.logoUrl ? `<img src="${escapeAttr(input.logoUrl)}" alt="">` : "<i></i>";
  const wm = input.watermark ? '<div class="wm">Field copy \u2014 not yet reviewed</div>' : "";
  return `<div class="band">
  <div class="logo">${logo}<div>${escapeHtml(input.companyName)}</div></div>
  <div class="rt"><h1>${escapeHtml(input.title)}</h1><div class="co">${escapeHtml(input.jobLine)}</div>${wm}</div>
</div>`;
}
function renderFooter(input) {
  const left = input.footerText ? escapeHtml(input.footerText) : "&nbsp;";
  const v = input.version !== void 0 && input.version > 1 ? ` \xB7 v${input.version}` : "";
  return `<div class="foot"><span>${left}</span><span>Generated by SiteDay</span><span>Report ${escapeHtml(input.reportId)}${v}</span></div>`;
}

// src/report-template/daily.ts
function photosOnNewPage(data, config) {
  if (!config.sections.photos || data.photos.length === 0) return false;
  if (data.photos.length >= 4) return true;
  const rows = data.quantities.length + data.crew.length + data.equipment.length + (config.sections.delays ? data.events.length : 0) + (config.sections.notes && data.entry.notes ? 3 : 0);
  return rows > 14;
}
function renderDailyHtml(data, config) {
  const e = data.entry;
  const title = `Daily Report \u2014 ${data.job.name} \u2014 ${e.date}`;
  const jobLine = data.job.clientName ? `${data.job.name} \xB7 ${data.job.clientName}` : data.job.name;
  const footerText = data.company.footerText ?? config.footerText ?? "";
  let status = statusLabel(e.status);
  const stamp = e.status === "approved" || e.status === "synced" ? e.approvedAt ?? e.finishedAt : e.finishedAt;
  if (stamp) status += ` \xB7 ${fmtTime(stamp, data.timezone)}`;
  if (e.version > 1) status += ` \xB7 v${e.version}`;
  const weather = config.sections.weather ? weatherLabel(e.weatherCondition, e.temperatureF) : "";
  const meta = [
    `<div><span>Date</span><b>${escapeHtml(fmtLongDate(e.date))}</b></div>`,
    `<div><span>Foreman</span><b>${escapeHtml(e.foremanName)}</b></div>`,
    config.sections.weather ? `<div><span>Weather</span><b>${escapeHtml(weather || "\u2014")}</b></div>` : "",
    `<div><span>Status</span><b>${escapeHtml(status)}</b></div>`
  ].join("");
  const sections = [];
  sections.push(renderQuantities(data, config));
  sections.push(renderCrewEquipment(data));
  if (config.sections.delays && data.events.length > 0) sections.push(renderEvents(data));
  if (config.sections.notes && e.notes) {
    sections.push(`<h2>Notes</h2><p class="pnotes">${escapeHtml(e.notes)}</p>`);
  }
  if (config.sections.photos && data.photos.length > 0) sections.push(renderPhotos(data, config));
  if (config.showCostEstimates && data.costEstimates) {
    sections.push(
      `<p class="cost">Labor cost estimate $${escapeHtml(fmtQty(data.costEstimates.labor))} \xB7 Machine cost estimate $${escapeHtml(
        fmtQty(data.costEstimates.machine)
      )} (from default hourly costs)</p>`
    );
  }
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>${baseCss({
    header: `Daily Production Report \xB7 ${jobLine} \xB7 ${fmtLongDate(e.date)}`,
    footer: { left: footerText, right: `Report ${data.reportId}${e.version > 1 ? ` \xB7 v${e.version}` : ""}` }
  })}</style>
</head>
<body>
<div class="page">
${renderBand({ companyName: data.company.name, logoUrl: data.company.logoUrl, title: "Daily Production Report", jobLine, watermark: data.watermark })}
<div class="meta">${meta}</div>
${sections.join("\n")}
</div>
${renderFooter({ footerText, reportId: data.reportId, version: e.version })}
</body>
</html>`;
}
function renderQuantities(data, config) {
  if (data.quantities.length === 0) return '<h2>Quantities</h2><p class="muted">No quantities logged.</p>';
  const showToDate = config.sections.toDate;
  const head = [
    "<th>Code</th>",
    "<th>Description</th>",
    '<th class="r">Today</th>',
    "<th>Unit</th>",
    '<th class="r">Plan / day</th>',
    '<th class="r">%</th>',
    showToDate ? '<th class="r">To-date / planned</th>' : ""
  ].join("");
  const rows = data.quantities.map((q) => {
    const hasPlan = q.planPerDay !== null && q.planPerDay !== void 0;
    const pct = hasPlan && q.pct !== null && q.pct !== void 0 ? q.pct : null;
    const band = pct === null ? "none" : q.band ?? bandFor(pct);
    const cls = pct === null ? "" : BAND_CLASS[band] ?? "";
    const pctCell = pct === null ? "\u2014" : `${pct}%`;
    let toDate = "";
    if (showToDate) {
      const td = q.toDate !== null && q.toDate !== void 0 ? fmtQty(q.toDate) : null;
      const pt = q.plannedTotal !== null && q.plannedTotal !== void 0 ? fmtQty(q.plannedTotal) : null;
      const text = td !== null && pt !== null ? `${td} / ${pt}` : td !== null ? td : "\u2014";
      toDate = `<td class="r">${escapeHtml(text)}</td>`;
    }
    return `<tr><td>${escapeHtml(q.code)}</td><td>${escapeHtml(q.description)}</td><td class="r">${escapeHtml(
      fmtQty(q.quantity)
    )}</td><td>${escapeHtml(q.unit)}</td><td class="r">${hasPlan ? escapeHtml(fmtQty(q.planPerDay)) : "\u2014"}</td><td class="r${cls ? ` ${cls}` : ""}">${pctCell}</td>${toDate}</tr>`;
  }).join("");
  return `<h2>Quantities</h2>
<table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}
function renderCrewEquipment(data) {
  const crewRows = data.crew.map((c) => `<tr><td>${escapeHtml(c.name)}</td><td class="r">${escapeHtml(fmtHours(c.hours))}</td></tr>`).join("");
  const crewTotal = `<tr class="tot"><td>${data.crew.length} ${data.crew.length === 1 ? "person" : "people"}</td><td class="r">${escapeHtml(
    fmtHours(data.crewTotalHours)
  )}</td></tr>`;
  const eqRows = data.equipment.length === 0 ? '<tr><td colspan="3" class="muted">None</td></tr>' : data.equipment.map(
    (m) => `<tr><td>${escapeHtml(m.name)}</td><td>${escapeHtml(m.unitNumber ?? "")}</td><td class="r">${escapeHtml(
      fmtHours(m.hours)
    )}</td></tr>`
  ).join("");
  const eqTotal = `<tr class="tot"><td colspan="2">${data.equipment.length} ${data.equipment.length === 1 ? "machine" : "machines"}</td><td class="r">${escapeHtml(fmtHours(data.equipmentTotalHours))}</td></tr>`;
  return `<div class="cols">
<div><h2>Crew</h2><table><thead><tr><th>Name</th><th class="r">Hours</th></tr></thead><tbody>${crewRows}${crewTotal}</tbody></table></div>
<div><h2>Equipment</h2><table><thead><tr><th>Machine</th><th>Unit #</th><th class="r">Hours</th></tr></thead><tbody>${eqRows}${eqTotal}</tbody></table></div>
</div>`;
}
function renderEvents(data) {
  const rows = data.events.map(
    (ev) => `<tr><td style="width:120px">${escapeHtml(ev.label)}</td><td style="width:70px" class="r">${escapeHtml(
      durationLabel(ev.durationMinutes)
    )}</td><td>${escapeHtml(ev.description ?? "")}</td></tr>`
  ).join("");
  return `<h2>Delays &amp; extra work</h2>
<table><thead><tr><th>Type</th><th class="r">Duration</th><th>Description</th></tr></thead><tbody>${rows}</tbody></table>`;
}
function renderPhotos(data, config) {
  const cls = ["photos", config.photoSize === "small" ? "small" : "large"].join(" ");
  const breakCls = photosOnNewPage(data, config) ? " break" : "";
  const cols = config.photoSize === "small" ? 3 : 2;
  const figures = data.photos.map((ph) => {
    const time = fmtTime(ph.takenAt, data.timezone);
    const caption = ph.caption ? `${ph.caption} \xB7 ${time}` : time;
    return `<figure><img src="${escapeAttr(ph.url)}" alt=""><figcaption>${escapeHtml(caption)}</figcaption></figure>`;
  }).join("");
  return `<div class="photos-block${breakCls}"><h2>Photos (${data.photos.length})</h2>
<div class="${cls}" style="grid-template-columns: repeat(${cols}, 1fr)">${figures}</div></div>`;
}

// src/report-template/weekly-math.ts
import Decimal3 from "decimal.js";
var STATUS_RANK = { none: 0, draft: 1, finished: 2, needs_review: 3, approved: 4 };
function toDayStatus(status) {
  switch (status) {
    case "approved":
    case "synced":
      return "approved";
    case "needs_review":
      return "needs_review";
    case "finished":
      return "finished";
    case "draft":
      return "draft";
    default:
      return "none";
  }
}
var zeros = () => Array.from({ length: 7 }, () => new Decimal3(0));
var str = (xs) => xs.map((x) => x.toString());
function weeklyMatrices(data) {
  const dayIndex = new Map(data.days.map((d, i) => [d, i]));
  const qty = /* @__PURE__ */ new Map();
  for (const c of data.costCodes) {
    qty.set(c.code, { code: c.code, description: c.description, unit: c.unit, perDay: [], total: "0", plannedTotal: c.plannedTotal, toDate: c.toDate, acc: zeros() });
  }
  const extra = [];
  const labor = /* @__PURE__ */ new Map();
  const equipment = /* @__PURE__ */ new Map();
  const events = /* @__PURE__ */ new Map();
  const dayStatus = Array.from({ length: 7 }, () => "none");
  const foremen = /* @__PURE__ */ new Set();
  let photosCount = 0;
  for (const entry of data.entries) {
    const di = dayIndex.get(entry.date);
    if (di === void 0) continue;
    foremen.add(entry.foremanName);
    photosCount += entry.photoCount;
    const s = toDayStatus(entry.status);
    if (STATUS_RANK[s] > STATUS_RANK[dayStatus[di] ?? "none"]) dayStatus[di] = s;
    for (const q of entry.quantities) {
      let row = qty.get(q.code);
      if (!row) {
        row = { code: q.code, description: q.description, unit: q.unit, perDay: [], total: "0", acc: zeros() };
        qty.set(q.code, row);
        extra.push(q.code);
      }
      row.acc[di] = (row.acc[di] ?? new Decimal3(0)).plus(q.quantity);
    }
    for (const l of entry.labor) {
      const row = labor.get(l.name) ?? { acc: zeros() };
      row.acc[di] = (row.acc[di] ?? new Decimal3(0)).plus(l.hours);
      labor.set(l.name, row);
    }
    for (const m of entry.equipment) {
      const key = m.unitNumber ? `${m.name} #${m.unitNumber}` : m.name;
      const row = equipment.get(key) ?? { name: m.name, unitNumber: m.unitNumber ?? null, acc: zeros() };
      row.acc[di] = (row.acc[di] ?? new Decimal3(0)).plus(m.hours);
      equipment.set(key, row);
    }
    for (const ev of entry.events) {
      const row = events.get(ev.type) ?? { type: ev.type, label: ev.label, count: 0, totalMinutes: 0 };
      row.count += 1;
      row.totalMinutes += ev.durationMinutes ?? 0;
      events.set(ev.type, row);
    }
  }
  const orderedCodes = [...data.costCodes.map((c) => c.code), ...extra.sort((a, b) => a.localeCompare(b))];
  const seen = /* @__PURE__ */ new Set();
  const quantities = [];
  for (const code of orderedCodes) {
    if (seen.has(code)) continue;
    seen.add(code);
    const row = qty.get(code);
    if (!row) continue;
    const { acc, ...rest } = row;
    quantities.push({ ...rest, perDay: str(acc), total: sumDecimals(acc).toString() });
  }
  const byName = (a, b) => a.localeCompare(b);
  const laborRows = [...labor.entries()].sort(([a], [b]) => byName(a, b)).map(([name, r]) => ({ name, perDay: str(r.acc), total: sumDecimals(r.acc).toString() }));
  const equipmentRows = [...equipment.entries()].sort(([a], [b]) => byName(a, b)).map(([, r]) => ({
    name: r.name,
    unitNumber: r.unitNumber,
    perDay: str(r.acc),
    total: sumDecimals(r.acc).toString()
  }));
  const columnTotals = (rows) => {
    const acc = zeros();
    for (const r of rows) r.perDay.forEach((v, i) => acc[i] = (acc[i] ?? new Decimal3(0)).plus(v));
    return { perDay: str(acc), total: sumDecimals(acc).toString() };
  };
  return {
    quantities,
    labor: laborRows,
    laborTotals: columnTotals(laborRows),
    equipment: equipmentRows,
    equipmentTotals: columnTotals(equipmentRows),
    events: [...events.values()].sort((a, b) => a.label.localeCompare(b.label)),
    dayStatus,
    photosCount,
    entryCount: data.entries.length,
    foremanCount: foremen.size
  };
}

// src/report-template/weekly.ts
function renderWeeklyHtml(data, config) {
  const m = weeklyMatrices(data);
  const first = data.days[0] ?? data.weekStart;
  const last = data.days[6] ?? data.weekStart;
  const title = `Weekly Report \u2014 ${data.job.name} \u2014 ${data.weekStart}`;
  const jobLine = data.job.clientName ? `${data.job.name} \xB7 ${data.job.clientName}` : data.job.name;
  const footerText = data.company.footerText ?? config.footerText ?? "";
  const weekLabel = `${fmtShortDate(first)} \u2013 ${fmtShortDate(last, true)}`;
  const entriesLabel = `${m.entryCount} ${m.entryCount === 1 ? "entry" : "entries"} \xB7 ${m.foremanCount} ${m.foremanCount === 1 ? "foreman" : "foremen"}`;
  const meta = [
    `<div><span>Week</span><b>${escapeHtml(weekLabel)}</b></div>`,
    `<div><span>Job</span><b>${escapeHtml(data.job.name)}</b></div>`,
    `<div><span>Client</span><b>${escapeHtml(data.job.clientName ?? "\u2014")}</b></div>`,
    `<div><span>Entries</span><b>${escapeHtml(entriesLabel)}</b></div>`
  ].join("");
  const dayHeads = data.days.map((d) => `<th class="r">${escapeHtml(fmtDayHead(d))}</th>`).join("");
  const statusRow = (leading) => `<tr class="status-row"><td colspan="${leading}"></td>${m.dayStatus.map((s) => `<td class="r">${escapeHtml(statusShort(s))}</td>`).join("")}<td></td>${config.sections.toDate ? "<td></td>" : ""}</tr>`;
  const showToDate = config.sections.toDate;
  const qtyRows = m.quantities.length === 0 ? `<tr><td colspan="${10 + (showToDate ? 1 : 0)}" class="muted">No quantities logged this week.</td></tr>` : m.quantities.map((q) => {
    let toDate = "";
    if (showToDate) {
      const pt = q.plannedTotal !== null && q.plannedTotal !== void 0 ? fmtQty(q.plannedTotal) : null;
      const td = q.toDate !== null && q.toDate !== void 0 ? fmtQty(q.toDate) : null;
      const text = pt !== null && td !== null ? `${pt} / ${td}` : pt !== null ? pt : td !== null ? `\u2014 / ${td}` : "\u2014";
      toDate = `<td class="r">${escapeHtml(text)}</td>`;
    }
    return `<tr><td>${escapeHtml(q.code)}</td><td>${escapeHtml(q.description)}</td><td>${escapeHtml(q.unit)}</td>${q.perDay.map((v) => `<td class="r">${v === "0" ? '<span class="muted">\xB7</span>' : escapeHtml(fmtQty(v))}</td>`).join("")}<td class="r"><b>${escapeHtml(fmtQty(q.total))}</b></td>${toDate}</tr>`;
  }).join("");
  const table1 = `<h2>Quantities</h2>
<table><thead><tr><th>Code</th><th>Description</th><th>Unit</th>${dayHeads}<th class="r">Total</th>${showToDate ? '<th class="r">Planned / to-date</th>' : ""}</tr>${statusRow(3)}</thead><tbody>${qtyRows}</tbody></table>`;
  const hoursTable = (label, rows, totals, unitCol, cells) => {
    const lead = unitCol ? 2 : 1;
    const body = rows.length === 0 ? `<tr><td colspan="${lead + 8}" class="muted">None</td></tr>` : rows.map(
      (r) => `<tr>${cells(r)}${r.perDay.map((v) => `<td class="r">${v === "0" ? '<span class="muted">\xB7</span>' : escapeHtml(fmtHours(v))}</td>`).join("")}<td class="r"><b>${escapeHtml(fmtHours(r.total))}</b></td></tr>`
    ).join("");
    const tot = `<tr class="tot"><td colspan="${lead}">Total</td>${totals.perDay.map((v) => `<td class="r">${escapeHtml(fmtHours(v))}</td>`).join("")}<td class="r">${escapeHtml(fmtHours(totals.total))}</td></tr>`;
    return `<h2>${escapeHtml(label)}</h2>
<table><thead><tr><th>${unitCol ? "Machine</th><th>Unit #" : "Name"}</th>${dayHeads}<th class="r">Total</th></tr></thead><tbody>${body}${tot}</tbody></table>`;
  };
  const table2 = hoursTable("Labor hours", m.labor, m.laborTotals, false, (r) => `<td>${escapeHtml(r.name)}</td>`);
  const table3 = hoursTable("Equipment hours", m.equipment, m.equipmentTotals, true, (r) => {
    const unit = "unitNumber" in r ? r.unitNumber ?? "" : "";
    return `<td>${escapeHtml(r.name)}</td><td>${escapeHtml(unit)}</td>`;
  });
  const table4 = m.events.length === 0 ? "" : `<h2>Delays &amp; extra work</h2>
<table><thead><tr><th>Type</th><th class="r">Count</th><th class="r">Total time</th></tr></thead><tbody>${m.events.map(
    (ev) => `<tr><td>${escapeHtml(ev.label)}</td><td class="r">${ev.count}</td><td class="r">${escapeHtml(
      durationLabel(ev.totalMinutes)
    )}</td></tr>`
  ).join("")}</tbody></table>`;
  const included = [...data.entries].sort((a, b) => a.date === b.date ? a.foremanName.localeCompare(b.foremanName) : a.date.localeCompare(b.date)).map(
    (en) => `<li>${escapeHtml(fmtShortDate(en.date))} \xB7 ${escapeHtml(en.foremanName)} \xB7 ${escapeHtml(statusLabel(en.status))}${en.version > 1 ? ` \xB7 v${en.version}` : ""}</li>`
  ).join("");
  const entriesSection = `<div class="cols"><div><h2>Entries included</h2>${data.entries.length === 0 ? '<p class="muted">No entries this week.</p>' : `<ul style="margin:0;padding-left:16px">${included}</ul>`}</div><div><h2>Photos</h2><p style="margin:0">Photos: ${m.photosCount}</p></div></div>`;
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>${baseCss({
    landscape: true,
    header: `Weekly Production Report \xB7 ${jobLine} \xB7 week of ${fmtLongDate(data.weekStart)}`,
    footer: { left: footerText, right: `Report ${data.reportId}` }
  })}</style>
</head>
<body>
<div class="page">
${renderBand({ companyName: data.company.name, logoUrl: data.company.logoUrl, title: "Weekly Production Report", jobLine })}
<div class="meta">${meta}</div>
${table1}
${table2}
${table3}
${table4}
${entriesSection}
</div>
${renderFooter({ footerText, reportId: data.reportId })}
</body>
</html>`;
}

// src/report-template/csv.ts
var WEEKLY_CSV_COLUMNS = [
  "job",
  "week_start",
  "date",
  "entry_id",
  "foreman",
  "status",
  "section",
  "code_or_name",
  "unit",
  "value",
  "notes"
];
function csvEscape(value) {
  if (value === null || value === void 0) return "";
  const s = String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csvRow(fields) {
  return fields.map(csvEscape).join(",");
}
function buildWeeklyCsv(data) {
  const lines = [WEEKLY_CSV_COLUMNS.join(",")];
  const entries = [...data.entries].sort(
    (a, b) => a.date === b.date ? a.foremanName.localeCompare(b.foremanName) : a.date.localeCompare(b.date)
  );
  for (const e of entries) {
    const base = [data.job.name, data.weekStart, e.date, e.id, e.foremanName, e.status];
    for (const q of e.quantities) lines.push(csvRow([...base, "quantity", q.code, q.unit, q.quantity, ""]));
    for (const l of e.labor) lines.push(csvRow([...base, "labor", l.name, "HR", l.hours, ""]));
    for (const m of e.equipment) {
      lines.push(csvRow([...base, "equipment", m.unitNumber ? `${m.name} #${m.unitNumber}` : m.name, "HR", m.hours, ""]));
    }
    for (const ev of e.events) {
      lines.push(csvRow([...base, "event", ev.label, "MIN", ev.durationMinutes ?? "", ev.description ?? ""]));
    }
  }
  return `${lines.join("\r\n")}\r
`;
}
export {
  ApiErrorSchema,
  ApproveWeekRequest,
  AuthUser,
  BAND_CLASS,
  BandName,
  BandSchema,
  COMPANY_LOGO_CONTENT_TYPES,
  COMPANY_LOGO_FIELD,
  COMPANY_LOGO_MAX_BYTES,
  COMPANY_LOGO_MAX_EDGE_PX,
  CompanyLogoResponse,
  CompanyPatch,
  CompanySchema,
  CostCodeCreate,
  CostCodeOrderRequest,
  CostCodePatch,
  CrewLineDoc,
  CrewLineSchema,
  DEFAULT_BANDS,
  DEFAULT_UNITS_ENABLED,
  DailyReportData,
  DailyReportEvent,
  DailyReportPhoto,
  DailyReportQuantity,
  DashboardFlagsResponse,
  DashboardTodayResponse,
  DateOnly,
  DayEntryDocument,
  DayEntrySchema,
  DecimalString,
  ERROR_CODES,
  EVENT_LABELS,
  EXPORT_CSV_FILES,
  EXPORT_LINK_TTL_DAYS,
  Email,
  EmailReportRequest,
  EntriesQuery,
  EntryEventSchema,
  EntryFull,
  EntryListItem,
  EntryPatchRequest,
  EntryRevisionSchema,
  EntryStatus,
  EquipmentCreate,
  EquipmentLineDoc,
  EquipmentLineSchema,
  EquipmentPatch,
  EquipmentQuery,
  EquipmentResponse,
  EquipmentSchema,
  ErrorCode,
  EventDoc,
  EventType,
  ExportCounts,
  ExportCreateResponse,
  ExportStatus,
  ExportStatusValue,
  HTTP_STATUS_FOR_CODE,
  HoursString,
  IsoTimestamp,
  JobCostCodeSchema,
  JobCreate,
  JobDocument,
  JobForemanSchema,
  JobPatch,
  JobResponse,
  JobSchema,
  JobStatus,
  JobToDateResponse,
  JobsQuery,
  JwtClaims,
  LOGIN_ROLES,
  ListQuery,
  LogoutRequest,
  MeResponse,
  NonNegativeDecimalString,
  NullableIsoTimestamp,
  OtpRequest,
  OtpVerifyRequest,
  OutboxItem,
  PHOTO_CONTENT_TYPES,
  PHOTO_MAX_BYTES,
  Paginated,
  PasswordLoginRequest,
  Phone,
  PhotoCompleteRequest,
  PhotoDoc,
  PhotoSchema,
  PhotoUploadUrlRequest,
  PhotoUploadUrlResponse,
  PhotoUrlResponse,
  ProductionBandsSchema,
  QboBillableStatus,
  QboCandidate,
  QboCandidates,
  QboEnvironment,
  QboExportMode,
  QboExportPreview,
  QboExportPreviewEntry,
  QboExportPreviewRow,
  QboExportRequest,
  QboItemSuggestion,
  QboLaborCostCodeRule,
  QboLineStatus,
  QboMappingsRequest,
  QboMappingsResponse,
  QboNameOf,
  QboQueuedResponse,
  QboRetryRequest,
  QboSettingsPatch,
  QboStatus,
  QboSyncDownload,
  QboSyncFailure,
  QboSyncLog,
  QboSyncLogKind,
  QboSyncLogStatus,
  QboSyncLogsResponse,
  QuantityLineDoc,
  QuantityLineSchema,
  REJECTED_BACKOFF_MINUTES,
  REJECTED_MAX_ATTEMPTS,
  ReconcileRequest,
  RefreshRequest,
  RefreshResponse,
  RegisterDeviceRequest,
  ReportCompany,
  ReportConfigSchema,
  ReportEmailStatus,
  ReportJob,
  ResolveConflictRequest,
  RevisionSource,
  Role,
  SYNC_PUSH_MAX_BYTES,
  SYNC_PUSH_MAX_ITEMS,
  SetForemenRequest,
  SupportAdmin,
  SupportAdoption,
  SupportAuditEvent,
  SupportCompanyDetail,
  SupportCompanyJob,
  SupportCompanyList,
  SupportCompanyPatch,
  SupportCompanyRow,
  SupportCompanyUser,
  SupportCreateCompanyRequest,
  SupportCreateCompanyResponse,
  SupportHandoffRequest,
  SupportHandoffResponse,
  SupportImpersonateResponse,
  SupportJwtClaims,
  SupportLoginRequest,
  SupportLoginResponse,
  SupportSyncLog,
  SyncColumns,
  SyncPullQuery,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  SyncPushResult,
  TimeOfDay,
  Timezone,
  ToDateCostCode,
  ToDateSeriesPoint,
  TodayForemanRow,
  TodayJobRow,
  TodayQuantity,
  TokenResponse,
  UNITS,
  UNIT_LABELS,
  UnitSchema,
  UserCreate,
  UserPatch,
  UserResponse,
  UserSchema,
  UsersQuery,
  Uuid,
  WEEKLY_CSV_COLUMNS,
  WeatherCondition,
  WeeklyReportData,
  WeeklyReportEntry,
  WeeklyReportQuery,
  bandFor,
  baseCss,
  buildWeeklyCsv,
  coalesceOutbox,
  cssString,
  csvEscape,
  csvRow,
  dec,
  durationLabel,
  escapeAttr,
  escapeHtml,
  estimateCost,
  fmtDateTime,
  fmtDayHead,
  fmtHours,
  fmtLongDate,
  fmtQty,
  fmtShortDate,
  fmtTime,
  formatHours,
  formatQuantity,
  isIanaTimezone,
  isSuspiciousQuantity,
  isUuid,
  isUuidV7,
  newId,
  percentOfPlan,
  photosOnNewPage,
  plannedPerDay,
  productionFor,
  renderBand,
  renderDailyHtml,
  renderFooter,
  renderWeeklyHtml,
  statusLabel,
  statusShort,
  sumDecimals,
  validateFinishDay,
  weatherLabel,
  weeklyMatrices
};
//# sourceMappingURL=index.js.map